<script setup lang="ts">
import BlogBreadcrumb from '@/components/blog/BlogBreadcrumb.vue'
import BlogCard from '@/components/blog/BlogCard.vue'
import BlogFAQ from '@/components/blog/BlogFaq.vue'
import type { Blog } from '@/data/blogs'
import { formatDateToISO, getBlogBySlug, getBlogFolderByCategory } from '@/data/blogs'
import { blogs } from '@/data/blogs'
import { getLatestBlogs } from '@/data/blogs'
import { easeOut, motion } from 'motion-v'

const route = useRoute()

// Get the slug from the URL (e.g., "why-restaurant-marketing-feels-complicated")
const slugFromUrl = route.path.split('/').pop()

const lastThreeBlogs = getLatestBlogs(
  blogs.filter(blog => blog.slug !== slugFromUrl),
  3,
)

const blog = getBlogBySlug(slugFromUrl?.toString() || '')

const headlineVariants = {
  initial: { opacity: 0, y: '10%' },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easeOut } },
}

const paragraphVariants = {
  initial: { opacity: 0, y: 20 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.4, ease: easeOut },
    }
  },
}

const blogVariants = {
  initial: { opacity: 0, y: 50 },
  animate: (custom: unknown) => {
    const i = Number(custom)
    return {
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: easeOut },
    }
  },
}

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  'headline': blog?.seo?.title,
  'image': [
    blog?.image,
  ],
  'datePublished': formatDateToISO(blog?.seo?.datePublished || ''),
  // 'dateModified': blog?.seo?.dateModified,
  // 'author': blog?.seo?.author?.map(a => ({
  //   '@type': a['@type'],
  //   'name': a.name,
  //   'url': a.url,
  // })) || [],
}

const faqSchema = computed(() => {
  if (!blog?.faq?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': blog.faq.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  }
})

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  'itemListElement': [
    {
      '@type': 'ListItem',
      'position': 1,
      'name': 'Home',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/`,
    },
    {
      '@type': 'ListItem',
      'position': 2,
      'name': 'Blog',
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog`,
    },
    {
      '@type': 'ListItem',
      'position': 3,
      'name': blog?.category,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog?category=${encodeURIComponent(blog?.category || '')}`,
    },
    {
      '@type': 'ListItem',
      'position': 4,
      'name': blog?.title,
      'item': `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
    },
  ],
}

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/blog/${getBlogFolderByCategory(blog?.category || '')}/${slugFromUrl}`,
  }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(blogSchema), // BlogPosting
    },
    blog?.faq?.length
      ? {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(faqSchema.value), // FAQPage
        }
      : null,
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbSchema),
    },
  ].filter(Boolean),
})

useSeoMeta({
  title: blog?.seo?.title,
  description: blog?.seo?.description,
  ogTitle: blog?.seo?.title,
  ogDescription: blog?.seo?.description,
  ogImage: blog?.seo?.ogImage,
})
</script>
<template>
  <div class="isolate">
    <LayoutNavbar />

    <div
      class="mb-0 bg-gradient-to-b from-[#f8f8f0] from-50% to-[#f8f8f0] pt-4  md:shadow-2xl md:shadow-black/10"
    >
      <section class="mx-auto mb-8 max-w-screen-xl px-4 md:px-8 lg:max-w-screen-xl xl:px-8">
        <BlogBreadcrumb :blog="blog as Blog" />
        <motion.h1
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-3xl font-medium text-gray-700 lg:text-4xl"
        >
          {{ blog?.title }}
        </motion.h1>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-gray-500"
        >
          <router-link
            :to="{ path: '/blog', query: { category: blog?.category } }"
            class="text-[#FF7118] underline"
          >
            {{ blog?.category }}
          </router-link> • {{ blog?.date }}
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          30-Second Takeaways
        </motion.h2>

        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="pt-4 text-left text-gray-500"
        >
          AI in restaurant marketing uses automation and data to bring in new guests, drive repeat visits, and boost your visibility online.
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>AI in restaurant marketing helps restaurants attract new guests, increase repeat visits, and fill more tables automatically.
            </span>
          </li>
          <li>
            <span>
              The best AI tools improve visibility in Google search, where most dining decisions start.
            </span>
          </li>
          <li>
            <span>
              Automated email, text marketing, and review management increase revenue without adding manual work.

            </span>
          </li>
          <li>
            <span>
              AI helps capture high-margin catering and event revenue that is often missed.

            </span>
          </li>
          <li>
            <span>
              The right system runs in the background, turning online traffic into reservations.

            </span>
          </li>
        </motion.ul>
        <motion.img
          :initial="{ opacity: 0, y: 50 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
          :src="blog?.image"
          :alt="blog?.alt"
          class="mt-6 w-full rounded-[2.5rem] object-cover drop-shadow-lg"
        />
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI in restaurant marketing helps restaurants fill more tables by improving online visibility, automating guest marketing, and converting website traffic into reservations.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          According to <a
            href="https://www.deloitte.com/us/en/insights/industry/retail-distribution/ai-in-restaurants.html"
            target="_blank"
            class="text-[#FF7118] underline"
          >Deloitte</a>, 60% of operators believe AI will improve the guest experience, and 36% believe it will improve operations.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          But for independent restaurant owners, the real question isn’t what AI can do. It’s:
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          How can AI help fill more tables right now without creating more work?
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The right system doesn’t add complexity. It works quietly in the background, turning searches into guests and past visitors into repeat customers.
        </motion.p>
        <div class="mt-3 rounded-3xl border border-neutral-200 bg-white px-6 py-4">
          <motion.p
            :variants="paragraphVariants"
            :custom="0"
            initial="initial"
            while-in-view="animate"
            class="text-xl font-semibold leading-relaxed text-gray-800"
          >
            This isn’t just about being online. It’s about appearing at the top when someone searches “restaurants near me.”
          </motion.p>
        </div>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Does AI Help Restaurants Get More Customers?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <RouterLink
            :to="{ path: '/how-it-works' }"
            class="text-[#FF7118] underline"
          >
            <span class="text-[#FF7118] underline"> AI helps restaurants get more customers </span>
          </RouterLink>  by increasing visibility in search results, automating marketing, and making it easier for guests to book, order, or inquire.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI improves restaurant marketing by:
        </motion.p>

        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Ranking your restaurant higher in Google search
            </span>
          </li>
          <li>
            <span>
              Converting website visitors into reservations
            </span>
          </li>
          <li>
            <span>
              Re-engaging past guests automatically

            </span>
          </li>
          <li>
            <span>
              Increasing review volume and ratings

            </span>
          </li>
          <li>
            <span>
              Capturing catering and event inquiries

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          When these systems work together, restaurants consistently generate more traffic and more bookings.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Does AI Improve Restaurant SEO and Online Visibility?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI improves restaurant SEO by optimizing websites, updating content automatically, and strengthening ranking signals, like reviews and engagement.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Most dining decisions begin on Google. If your website is slow, outdated, or difficult to use, diners choose competitors instead.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI-powered SEO systems:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li><span>Build fast, mobile-optimized restaurant websites</span></li>
          <li>
            <span>
              Automatically update menus and content
            </span>
          </li>
          <li>
            <span>
              Improve Google Business Profile performance
            </span>
          </li>
          <li>
            <span>
              Strengthen local search rankings
            </span>
          </li>
          <li>
            <span>
              Ensure consistent business information across platforms
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          This isn’t just about being online. It’s about appearing at the top when someone searches “restaurants near me.”
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          AI vs. Traditional Restaurant Marketing: What’s the Difference?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The key difference between AI and traditional restaurant marketing is automation, speed, and consistency.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI-driven systems continuously run in the background, while traditional marketing requires manual effort and is often inconsistent.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-4 font-semibold text-gray-500"
        >
          AI vs. Traditional Restaurant Marketing
        </motion.p>
        <motion.div
          :initial="{ opacity: 0, y: 50 }"
          :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
          class="mb-6 mt-6 max-w-4xl overflow-hidden rounded-2xl border border-gray-200"
        >
          <!-- Header -->
          <div class="grid grid-cols-2 bg-gray-100 font-semibold text-gray-700">
            <div class="border-r border-gray-200 p-4">
              <p class="text-gray-500">
                Traditional Marketing
              </p>
            </div>
            <div class="p-4">
              <p class="text-gray-500">
                AI-Powered Marketing
              </p>
            </div>
          </div>

          <!-- Rows -->
          <div class="grid grid-cols-2 divide-x divide-gray-200">
            <motion.div
              v-for="(row, index) in [
                ['Manual campaigns that require constant effort', 'Automated campaigns that run continuously'],
                ['Inconsistent outreach based on available time', 'Consistent guest engagement every day'],
                ['Limited follow-up with past customers', 'Automated re-engagement via email and text'],
                ['Slow response to reviews and inquiries', 'Instant or near-instant responses and follow-ups'],
                ['Missed catering and event opportunities', 'Automated capture and follow-up of leads'],
                ['Static websites with infrequent updates', 'Continuously optimized, SEO-driven websites'],
              ]"
              :key="index"
              class="contents"
              :variants="paragraphVariants"
              :custom="index"
            >
              <!-- Left -->
              <div class="border-t border-gray-200 p-4">
                <p class="text-gray-500">
                  {{ row[0] }}
                </p>
              </div>

              <!-- Right -->
              <div class="border-l border-t border-gray-200 p-4">
                <p class="font-medium text-gray-500">
                  {{ row[1] }}
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          Why AI Outperforms Traditional Marketing
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI outperforms traditional restaurant marketing because it removes human bottlenecks and ensures consistent execution.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          With traditional marketing:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Efforts depend on time, staffing, and attention
            </span>
          </li>
          <li>
            <span>Follow-up is often delayed or missed
            </span>
          </li>
          <li>
            <span>Marketing stops when operations get busy
            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          With AI:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Marketing runs continuously in the background
            </span>
          </li>
          <li>
            <span>Guests are automatically re-engaged
            </span>
          </li>
          <li>
            <span>Opportunities are captured in real time
            </span>
          </li>
        </motion.ul>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          What This Means for Restaurants?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Restaurants using AI-driven marketing systems generate more consistent traffic, more repeat visits, and higher revenue.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Does AI Increase Repeat Customers for Restaurants?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI increases repeat customers by automatically sending targeted email and text marketing to past guests.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The most profitable guest is one who has already visited. But without follow-up, they forget.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI-powered retention marketing can:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Re-engage guests who haven’t visited recently
            </span>
          </li>
          <li>
            <span>Send birthday and promotional offers automatically

            </span>
          </li>
          <li>
            <span>Promote events and fill slower nights

            </span>
          </li>
          <li>
            <span>Maintain consistent communication without manual effort

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          When marketing runs automatically, your restaurant stays top-of-mind without adding work.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Does AI Improve Restaurant Reviews and Reputation?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI improves restaurant reviews by increasing review volume, speeding up responses, and optimizing reputation signals that influence rankings.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Online reviews directly impact both visibility and customer decisions.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI-driven reputation tools:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Automatically request Google reviews after visits
            </span>
          </li>
          <li>
            <span>
              Route negative feedback privately before it becomes public

            </span>
          </li>
          <li>
            <span>
              Help respond to reviews quickly and professionally

            </span>
          </li>
          <li>
            <span>
              Keep your Google Business Profile accurate and active

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          More recent, positive reviews lead to higher rankings—and more reservations.
        </motion.p>

        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How Does AI Help Capture Catering and Event Revenue?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI helps restaurants capture more catering and event revenue by streamlining inquiries, bookings, and follow-ups.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Catering and private events are often the highest-margin opportunities—but also the easiest to miss.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          AI systems help by:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Managing online catering and event requests
            </span>
          </li>
          <li>
            <span>
              Automating booking workflows

            </span>
          </li>
          <li>
            <span>
              Collecting guest data and deposits

            </span>
          </li>
          <li>
            <span>
              Following up with leads automatically

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Even one missed inquiry can mean thousands in lost revenue.
        </motion.p>

        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          What Should AI in Restaurant Marketing Actually Do?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The best AI for restaurant marketing should simplify operations while increasing revenue and visibility.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Effective AI systems should:
        </motion.p>
        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Improve Google search rankings

            </span>
          </li>
          <li>
            <span>
              Increase website traffic and conversions

            </span>
          </li>
          <li>
            <span>
              Automate repeat guest marketing

            </span>
          </li>
          <li>
            <span>
              Strengthen online reputation

            </span>
          </li>
          <li>
            <span>
              Capture reservations and event revenue

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If AI adds complexity, it’s not working.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If it fills tables without extra effort, it is.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          How to Use AI to Improve Restaurant Marketing?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          To use AI effectively in restaurant marketing, focus on systems that automate visibility, engagement, and conversion.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Follow these steps:
        </motion.p>
        <motion.ol
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          style="list-style: auto;"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside py-4 text-gray-500"
        >
          <li>
            <span>Use AI to optimize your website and SEO

            </span>
          </li>
          <li>
            <span>
              Automate email and text marketing campaigns

            </span>
          </li>
          <li>
            <span>
              Implement AI-driven review management

            </span>
          </li>
          <li>
            <span>
              Capture catering and event inquiries automatically

            </span>
          </li>
          <li>
            <span>
              Use one connected system instead of multiple tools

            </span>
          </li>
        </motion.ol>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          The goal isn’t more marketing. It’s smarter marketing.
        </motion.p>
        <motion.h2
          :variants="headlineVariants"
          initial="initial"
          while-in-view="animate"
          class="pt-6 text-left text-2xl font-medium text-gray-700 lg:text-3xl"
        >
          Where SpotHopper Fits In?
        </motion.h2>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          SpotHopper combines:
        </motion.p>

        <motion.ul
          :variants="paragraphVariants"
          :custom="1"
          initial="initial"
          while-in-view="animate"
          class="my-0 ml-10 mr-0 list-outside list-disc py-2 text-gray-500"
        >
          <li>
            <span>Restaurant website design

            </span>
          </li>
          <li>
            <span>
              Built-in SEO

            </span>
          </li>
          <li>
            <span>
              Google review management

            </span>
          </li>
          <li>
            <span>
              Email and text marketing

            </span>
          </li>
        </motion.ul>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <RouterLink :to="{ path: '/how-it-works' }">
            <span class="text-[#FF7118] underline">All in one connected system.</span>
          </RouterLink>
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          Instead of juggling tools, your marketing works as a single engine—attracting new guests, increasing repeat visits, and filling slow nights.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          If you want to compete with larger restaurant groups without adding more work, the answer isn’t more effort. It’s a smarter system.
        </motion.p>
        <motion.p
          :variants="paragraphVariants"
          :custom="0"
          initial="initial"
          while-in-view="animate"
          class="py-2 text-gray-500"
        >
          <RouterLink :to="{ path: '/free-demo' }">
            <span class="text-[#FF7118] underline">Book a demo</span>
          </RouterLink>
          and see how SpotHopper helps fill more tables automatically.
        </motion.p>
      </section>

      <div class="mb-8 w-full bg-white  pb-8 pt-8 md:rounded-[5rem] ">
        <BlogFAQ :items="blog?.faq" />
        <section class="mx-auto mb-8 px-2 lg:max-w-screen-xl 2xl:px-0">
          <div class="grid w-full grid-cols-1 gap-x-6 gap-y-12 py-6 md:grid-cols-3">
            <motion.div
              v-for="(item, index) in lastThreeBlogs"
              :key="item.slug"
              :custom="index"
              :variants="blogVariants"
              initial="initial"
              while-in-view="animate"
            >
              <BlogCard v-bind="item" />
            </motion.div>
          </div>
        </section>
        <section class="mx-auto px-2 lg:max-w-screen-xl lg:pb-16 2xl:px-0">
          <div class="relative overflow-clip rounded-3xl px-4 py-10 md:px-8 md:py-20">
            <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
              <motion.img
                :initial="{ opacity: 0, y: 50 }"
                :while-in-view="{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }"
                :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
                class="aspect-video size-full object-cover object-center"
                role="presentation"
              />
            </div>

            <div class="isolate grid place-items-center gap-5">
              <motion.h3
                :variants="headlineVariants"
                initial="initial"
                while-in-view="animate"
                class="max-w-screen-sm text-balance text-center text-3xl leading-tight text-white lg:text-5xl"
              >
                Ready to take your restaurant marketing to the next level?
              </motion.h3>

              <UiButton
                class="group w-full max-w-xs lg:max-w-max"
                as-child
              >
                <RouterLink :to="{ name: '/free-demo' }">
                  Book a Free Demo

                  <UiIcon
                    name="arrow-forward-rounded"
                    class="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </RouterLink>
              </UiButton>
            </div>
          </div>
        </section>
      </div>

      <LayoutFooter />
    </div>
  </div>
</template>
